import type { MaImage } from '@/lib/zybooks-api';
import { useContainerPosition, useMasonry, usePositioner, useResizeObserver, type RenderComponentProps } from 'masonic';
import type { RefObject } from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ImageMasonryCard } from './image-masonry-card';

interface ImageGridProps {
    images: MaImage[];
    scrollParentRef: RefObject<HTMLElement | null>;
    onEdit?: (image: MaImage) => void;
    onEditAltText?: (image: MaImage) => void;
    onDelete?: (image: MaImage) => void;
    onPreview?: (image: MaImage) => void;
    onPick?: (image: MaImage) => void;
    pickerSelected?: MaImage | null;
}

export function ImageGrid({ images, scrollParentRef, onEdit, onEditAltText, onDelete, onPreview, onPick, pickerSelected }: ImageGridProps) {
    const masonryContainerRef = useRef<HTMLElement | null>(null);
    const scrollStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [scrollTop, setScrollTop] = useState(0);
    const [viewportHeight, setViewportHeight] = useState(0);
    const [viewportWidth, setViewportWidth] = useState(0);
    const [isScrolling, setIsScrolling] = useState(false);

    useLayoutEffect(() => {
        const el = scrollParentRef.current;
        if (!el) return;
        if (el.clientHeight < 2) {
            el.style.minHeight = '100vh';
        }
    }, [scrollParentRef]);

    useEffect(() => {
        const el = scrollParentRef.current;
        if (!el) return;

        const sync = () => {
            setScrollTop(el.scrollTop);
            setViewportHeight(el.clientHeight);
            setViewportWidth(el.clientWidth);
            setIsScrolling(true);
            if (scrollStopTimerRef.current !== null) {
                clearTimeout(scrollStopTimerRef.current);
            }
            scrollStopTimerRef.current = setTimeout(() => {
                setIsScrolling(false);
                scrollStopTimerRef.current = null;
            }, 120);
        };

        sync(); // eslint-disable-line react-you-might-not-need-an-effect/no-external-store-subscription
        el.addEventListener('scroll', sync, { passive: true });
        const ro = new ResizeObserver(sync);
        ro.observe(el);

        return () => {
            el.removeEventListener('scroll', sync);
            ro.disconnect();
            if (scrollStopTimerRef.current !== null) {
                clearTimeout(scrollStopTimerRef.current);
            }
        };
    }, [scrollParentRef]);

    const containerPos = useContainerPosition(masonryContainerRef, [viewportWidth, viewportHeight]);

    const positionerWidth = Math.max(containerPos.width || viewportWidth, 1);
    const positioner = usePositioner(
        {
            width: positionerWidth,
            columnWidth: 300,
            columnGutter: 24,
        },
        [positionerWidth],
    );
    const resizeObserver = useResizeObserver(positioner);

    const RenderCell = useMemo(
        () =>
            function MasonryImageCell({ data }: RenderComponentProps<MaImage>) {
                return (
                    <ImageMasonryCard
                        data={data}
                        onEdit={onEdit}
                        onEditAltText={onEditAltText}
                        onDelete={onDelete}
                        onPreview={onPreview}
                        onPick={onPick}
                        pickerSelected={pickerSelected}
                    />
                );
            },
        [onEdit, onEditAltText, onDelete, onPreview, onPick, pickerSelected],
    );

    return useMasonry({
        positioner,
        resizeObserver,
        items: images,
        itemKey: (data: MaImage) => data.ma_image_id,
        height: Math.max(viewportHeight, 1),
        scrollTop,
        isScrolling,
        containerRef: masonryContainerRef,
        render: RenderCell,
    });
}
